/**
 * LinkValidityControl
 * Radio choice for a personal guest link's validity: "Permanent länk" or
 * "Giltig till" with a date. Used by the assignment composer and the link
 * section on Externa användare. Controlled.
 */
import React, { useRef } from 'react';
import { Form } from 'react-bootstrap';
import { LinkValidity } from '../api/externalLinksApi';
import { defaultLinkValidity } from '../utils/linkValidity';
import { formatDateOnlyForStorage } from '../Helpers/DateUtils';

interface LinkValidityControlProps {
    /** null = nothing chosen yet (neither option selected) */
    value: LinkValidity | null;
    onChange: (value: LinkValidity) => void;
    disabled?: boolean;
    /** Unique suffix for the radio ids when several controls can coexist */
    idSuffix: string;
    /**
     * The date "Giltig till" starts from when picked (the suggestion or the
     * link's current end date); the default period only when absent.
     */
    expiringDefault?: string;
}

const LinkValidityControl: React.FC<LinkValidityControlProps> = ({ value, onChange, disabled, idSuffix, expiringDefault }) => {
    const expiring = value?.kind === 'expiring';
    const today = formatDateOnlyForStorage(new Date());

    // Switching to Permanent and back restores the last date shown, never a fresh +90 days.
    const lastDate = useRef<string | undefined>(undefined);
    if (expiring && value?.expiresAt) lastDate.current = value.expiresAt;
    const pickExpiring = () => {
        const expiresAt = lastDate.current ?? expiringDefault;
        onChange(expiresAt ? { kind: 'expiring', expiresAt } : defaultLinkValidity());
    };

    return (
        <div className="d-flex flex-column gap-2">
            <Form.Check
                type="radio"
                id={`link-validity-permanent-${idSuffix}`}
                name={`link-validity-${idSuffix}`}
                label="Permanent länk"
                checked={value?.kind === 'permanent'}
                onChange={() => onChange({ kind: 'permanent' })}
                disabled={disabled}
            />
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <Form.Check
                    type="radio"
                    id={`link-validity-expiring-${idSuffix}`}
                    name={`link-validity-${idSuffix}`}
                    label="Giltig till"
                    checked={expiring}
                    onChange={pickExpiring}
                    disabled={disabled}
                />
                <Form.Control
                    type="date"
                    size="sm"
                    style={{ width: 'auto' }}
                    aria-label="Giltig till datum"
                    value={expiring ? value?.expiresAt ?? '' : ''}
                    min={today}
                    onChange={(e) => onChange({ kind: 'expiring', expiresAt: e.target.value })}
                    disabled={disabled || !expiring}
                />
            </div>
        </div>
    );
};

export default LinkValidityControl;
